import { useState, useEffect } from 'react';
import { useNavigate, Outlet, NavLink } from 'react-router-dom';
import { assetUrl } from 'fusion-core';
import { Helmet } from 'fusion-plugin-react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faFolder, faUsers, faListAlt, faBars, faTimes, faAnglesLeft } from '@fortawesome/free-solid-svg-icons';

export default function Dashboard() {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
    const navigate = useNavigate();

    // Add window resize handler
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setIsSidebarCollapsed(false);
            } else {
                setIsSidebarCollapsed(true);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call on initial load
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/admin');
            }
        };
        checkAuth();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/admin');
    };

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    return (
        <>
            <Helmet>
                <link rel="stylesheet" href={assetUrl('./styles/Dashboard.css')} />
                <link rel="stylesheet" href={assetUrl('./styles/UserManagement.css')} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
                />
                <style>
                    {`
                    @font-face {
                        font-family: 'UberMoveTextRegular';
                        src: url(${assetUrl("../../assets/font/UberMoveTextRegular.otf")}) format('opentype');
                        font-weight: normal;
                        font-style: normal;
                    }

                    @font-face {
                        font-family: 'UberMoveBold';
                        src: url(${assetUrl("../../assets/font/UberMoveBold.otf")}) format('opentype');
                        font-weight: bold;
                        font-style: normal;
                    }

                    @font-face {
                        font-family: "UberMoveTextMedium";
                        src: url(${assetUrl("../../assets/font/UberMoveMedium.otf")}) format("opentype");
                        font-weight: 500;
                        font-style: normal;
                    }

                    body {
                        font-family: 'UberMoveTextRegular', sans-serif;
                    }
                    
                    button {
                        font-family: 'UberMoveTextRegular', sans-serif;
                    }

                    h1, h2, h3, h4 {
                        font-family: 'UberMoveBold', sans-serif;
                    }

                    .dashboard {
                        display: flex;
                        min-height: 100vh;
                        position: relative;
                        background: #f5f5f5;
                    }

                    .main-header {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 60px;
                        background: white;
                        display: flex;
                        align-items: center;
                        padding: 0 2rem;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        z-index: 1000;
                    }

                    .header-brand {
                        font-family: 'UberMoveBold', sans-serif;
                        font-size: 1.2rem;
                        color: #000000;
                    }

                    .sidebar {
                        width: 250px;
                        transition: all 0.3s ease;
                        background: #000000;
                        height: 100vh;
                        position: fixed;
                        left: 0;
                        top: 60px;
                        padding: 1rem;
                        box-shadow: 2px 0 5px rgba(0,0,0,0.1);
                        color: white;
                    }

                    .collapse-btn-container {
                        position: fixed;
                        left: 250px;
                        top: 70px;
                        transition: left 0.3s ease;
                        z-index: 1000;
                    }

                    .sidebar.collapsed + .collapse-btn-container {
                        left: 60px;
                    }

                    .collapse-btn {
                        width: 24px;
                        height: 24px;
                        border: none;
                        background: #000000;
                        border-radius: 50%;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        transition: transform 0.3s ease;
                    }

                    .collapse-btn:hover {
                        background: #333333;
                    }
                        
                     @media (max-width: 768px) {.collapse-btn:hover{
                         transform: translateX(10px) translateY(-50%) !important;
                    }}

                    @media (max-width: 768px) {
                        .collapse-btn-container {
                            display: none;
                        }
                    }

                    .sidebar-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: 2rem;
                        position: relative;
                    }

                    .sidebar-nav ul {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                    }

                    .sidebar-nav ul li {
                        margin-bottom: 0.5rem;
                    }

                    .main-content {
                        flex: 1;
                        margin-left: 250px;
                        margin-top: 60px;
                        transition: margin-left 0.3s ease;
                        min-height: calc(100vh - 60px);
                    }

                    .dashboard.sidebar-collapsed .main-content {
                        margin-left: 60px;
                    }

                    .dashboard-nav {
                        padding: 1rem 2rem;
                        background: white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        position: sticky;
                        top: 0;
                        z-index: 100;
                    }

                    .dashboard-content {
                        padding: 2rem;
                    }

                    .logout-btn {
                        padding: 0.5rem 1rem;
                        border: none;
                        background: #000000;
                        color: white;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background-color 0.3s;
                    }

                    .logout-btn:hover {
                        background: #333333;
                    }

                    .sidebar-nav ul li a {
                        color: #ffffff;
                        text-decoration: none;
                        padding: 0.8rem 1rem;
                        display: flex;
                        align-items: center;
                        border-radius: 4px;
                        transition: all 0.3s ease;
                    }

                    .sidebar-nav ul li a:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }

                    .sidebar-nav ul li a.active {
                        background-color: #ffffff;
                    }

                    .sidebar-nav ul li a.active .icon,
                    .sidebar-nav ul li a.active .label {
                        color: #000000;
                    }

                    .sidebar-nav .icon {
                        width: 24px;
                        text-align: center;
                        margin-right: 10px;
                        color: #ffffff;
                        transition: color 0.3s ease;
                    }

                    .sidebar-nav .label {
                        display: block;
                        color: #ffffff;
                        transition: color 0.3s ease;
                    }

                    /* Only hide labels when sidebar is collapsed */
                    .sidebar.collapsed .sidebar-nav .label {
                        display: none;
                    }

                    .sidebar.collapsed .sidebar-nav ul li a {
                        justify-content: center;
                        padding: 0.8rem;
                        width: 40px;
                        height: 40px;
                        margin: 0 auto;
                        border-radius: 8px;
                    }

                    .sidebar.collapsed .sidebar-nav ul li {
                        display: flex;
                        justify-content: center;
                        margin-bottom: 0.8rem;
                    }

                    .sidebar.collapsed .sidebar-nav .icon {
                        margin-right: 0;
                        width: auto;
                        font-size: 1.2rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    /* Adjust active state for collapsed sidebar */
                    .sidebar.collapsed .sidebar-nav ul li a.active {
                        background-color: #ffffff;
                        width: 40px;
                        height: 40px;
                    }

                    /* Mobile styles */
                    @media (max-width: 768px) {
                        .sidebar-nav .label {
                            display: block;
                        }

                        .sidebar.collapsed .sidebar-nav .label {
                            display: none;
                        }
                    }

                    /* Add hover tooltip for collapsed sidebar items */
                    .sidebar.collapsed .sidebar-nav ul li {
                        position: relative;
                    }

                    .sidebar.collapsed .sidebar-nav ul li a:hover::after {
                        content: attr(title);
                        position: absolute;
                        left: 100%;
                        top: 50%;
                        transform: translateY(-50%);
                        background: #000000;
                        color: white;
                        padding: 8px 16px;
                        border-radius: 6px;
                        font-size: 0.875rem;
                        white-space: nowrap;
                        margin-left: 15px;
                        z-index: 1000;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                        font-weight: 500;
                        letter-spacing: 0.2px;
                    }

                    /* Add arrow to tooltip */
                    .sidebar.collapsed .sidebar-nav ul li a:hover::before {
                        content: '';
                        position: absolute;
                        left: calc(100% + 5px);
                        top: 50%;
                        transform: translateY(-50%);
                        border: 6px solid transparent;
                        border-right-color: #000000;
                        z-index: 1000;
                    }

                    .sidebar-header h2 {
                        color: #ffffff;
                        margin: 0;
                    }

                    /* Mobile Responsive Styles */
                    @media (max-width: 768px) {
                        .sidebar {
                            transform: translateX(-100%);
                            z-index: 1000;
                            width: 250px;
                        }

                        .sidebar.collapsed {
                            transform: translateX(-100%);
                        }

                        .sidebar:not(.collapsed) {
                            transform: translateX(0);
                            width: 250px;
                            background: #000000;
                        }

                        .main-content {
                            margin-left: 0;
                            margin-top: 60px;
                        }

                        .dashboard.sidebar-collapsed .main-content {
                            margin-left: 0;
                        }

                        .dashboard-nav {
                            padding: 1rem;
                        }

                        .dashboard-content {
                            padding: 1rem;
                        }

                        /* Show mobile menu button */
                        .mobile-menu-btn {
                            display: block;
                            margin-right: 1rem;
                        }

                        .nav-brand {
                            font-size: 1.1rem;
                        }

                        .main-header {
                            padding: 0 1rem;
                        }
                    }

                    /* Hide mobile menu button on desktop */
                    .mobile-menu-btn {
                        display: none;
                    }

                    .mobile-menu-btn {
                        background: none;
                        border: none;
                        color: #000000;
                        font-size: 1.2rem;
                        cursor: pointer;
                        padding: 0.5rem;
                    }

                    .mobile-menu-btn:hover {
                        color: #333333;
                    }

                    .nav-brand {
                        font-family: 'UberMoveBold', sans-serif;
                        color: #000000;
                    }

                    .logout-btn {
                        padding: 0.5rem 1rem;
                        border: none;
                        background: #000000;
                        color: white;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background-color 0.3s;
                    }

                    .logout-btn:hover {
                        background: #333333;
                    }

                    .logout-btn {
                        padding: 0.5rem 1rem;
                        border: none;
                        background: #000000;
                        color: white;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background-color 0.3s;
                    }

                    .logout-btn:hover {
                        background: #333333;
                    }

                    .dashboard-nav {
                        padding: 1rem 2rem;
                        background: white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        position: sticky;
                        top: 0;
                        z-index: 100;
                    }

                    .mobile-menu-btn {
                        background: none;
                        border: none;
                        color: #000000;
                        font-size: 1.2rem;
                        cursor: pointer;
                        padding: 0.5rem;
                    }

                    .mobile-menu-btn:hover {
                        color: #333333;
                    }

                    .nav-brand {
                        font-family: 'UberMoveBold', sans-serif;
                        color: #000000;
                    }

                    .logout-btn {
                        padding: 0.5rem 1rem;
                        border: none;
                        background: #000000;
                        color: white;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background-color 0.3s;
                    }

                    .logout-btn:hover {
                        background: #333333;
                    }

                    .sidebar-nav .icon {
                        width: 24px;
                        text-align: center;
                        margin-right: 10px;
                        color: #ffffff;
                    }

                    .sidebar-header h2 {
                        color: #ffffff;
                        margin: 0;
                    }

                    /* Mobile Responsive Styles */
                    @media (max-width: 768px) {
                        .sidebar.mobile-visible {
                            transform: translateX(0);
                            width: 250px;
                            background: #000000;
                        }

                        .dashboard-nav {
                            background: white;
                        }
                    }
                    `}
                </style>
            </Helmet>
            <div className={`dashboard ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <header className="main-header">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                                className="mobile-menu-btn"
                                onClick={toggleSidebar}
                            >
                                <FontAwesomeIcon icon={!isSidebarCollapsed ? faTimes : faBars} />
                            </button>
                            <div className="header-brand">Admin Dashboard</div>
                        </div>
                        <button className="logout-btn" onClick={handleLogout}>Logout</button>
                    </div>
                </header>

                <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                    <nav className="sidebar-nav">
                        <ul>
                            <li>
                                <NavLink
                                    to="/admin/dashboard"
                                    end
                                    title="Dashboard"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    <span className="icon">
                                        <FontAwesomeIcon icon={faChartLine} />
                                    </span>
                                    <span className="label">Dashboard</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/admin/dashboard/users"
                                    title="Users"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    <span className="icon">
                                        <FontAwesomeIcon icon={faUsers} />
                                    </span>
                                    <span className="label">Users</span>
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/admin/dashboard/categories"
                                    title="Categories"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    <span className="icon">
                                        <FontAwesomeIcon icon={faFolder} />
                                    </span>
                                    <span className="label">Categories</span>
                                </NavLink>
                            </li>

                            <li>
                                <NavLink
                                    to="/admin/dashboard/scheme-features"
                                    title="Scheme Features"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    <span className="icon">
                                        <FontAwesomeIcon icon={faListAlt} />
                                    </span>
                                    <span className="label">Scheme Features</span>
                                </NavLink>
                            </li>

                            <li>
                                <NavLink
                                    to="/admin/dashboard/addons"
                                    title="Addons"
                                    className={({ isActive }) => isActive ? 'active' : ''}
                                >
                                    <span className="icon">
                                        <FontAwesomeIcon icon={faListAlt} />
                                    </span>
                                    <span className="label">Addons</span>
                                </NavLink>
                            </li>

                        </ul>
                    </nav>
                    <div className="sidebar-footer">
                        <button
                            className="collapse-btn"
                            onClick={toggleSidebar}
                            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        >
                            <FontAwesomeIcon
                                icon={isSidebarCollapsed ? faBars : faAnglesLeft}
                            />
                        </button>
                    </div>
                </aside>

                <main className="main-content">
                    <div className="dashboard-content">
                        <Outlet />
                    </div>
                </main>
            </div>
        </>
    );
}